import { QueryClient, UseMutationResult } from 'react-query';
import HttpServiceHelper from './httpServiceHelper';
import { _HttpMethods, _Payload, _VenueOption, _VenuePickerOption } from '../constants/staticTypes';
import {
    addVenueApi,
    checkDomainAvailabilityApi,
    deleteVenueApi,
    getOrgVenueListApi,
    getOrgVenueOptionsApi,
    getVenueCreationStatusApi,
    getVenueDetailByIdApi,
    updateVenueApi,
    updateVenueStatusApi,
} from '../constants/apiEndPoints';
import * as Yup from 'yup';
import { AxiosResponse } from 'axios';
import { getErrorMessage } from '../utils/common';
import { AlertVariant, ORG_ID_HEADER } from '../constants/constants';
import { VENUE_OPTIONS } from '../constants/queryKeys';
import logger from '../utils/logger';

export const getVenueList = (queryClient: QueryClient, page: number, limit: number, search?: string) => {
    let url = `${getOrgVenueListApi}?limit=${limit}&page=${page}`;
    if (search) {
        url += search;
    }

    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
    });
};

const VENUE_OPTIONS_STALE_TIME = 5 * 60 * 1000;

const fetchVenueOptions = async (queryClient: QueryClient) => {
    const response = await HttpServiceHelper({ url: getOrgVenueOptionsApi, method: _HttpMethods.GET, queryClient });
    if (response?.data?.data?.truncated) {
        logger.warn('Venue options response is truncated', response?.data?.data?.count);
    }
    return (response?.data?.data?.lists ?? []) as _VenueOption[];
};

// non-paginated venue list for pickers; cached per organization and shared across screens
export const getVenueOptions = async (queryClient: QueryClient): Promise<_VenuePickerOption[]> => {
    try {
        const venues = await queryClient.fetchQuery(
            [VENUE_OPTIONS, localStorage.getItem(ORG_ID_HEADER)],
            () => fetchVenueOptions(queryClient),
            {
                staleTime: VENUE_OPTIONS_STALE_TIME,
            }
        );
        return venues.map((venue) => ({ id: venue.id, venueName: venue.tenantName, isActive: venue.isActive }));
    } catch (error) {
        logger.log('Failed to fetch venue options', error);
        return [];
    }
};

export const invalidateVenueOptions = (queryClient: QueryClient) => queryClient.invalidateQueries(VENUE_OPTIONS);

export type _VenueFormType = {
    id: string;
    tenantName: string;
    isActive: boolean;
    contactName: string;
    email: string;
    primaryPhone: string;
    secondaryPhone: string;
    address: string;
    city: string;
    zip: string;
    state: string;
    country: string;
    domainName: string;
    webLogo: string;
    mobileLogo: string;
};

export const initialVenueFormDataValue: _VenueFormType = {
    id: '',
    tenantName: '',
    isActive: true,
    contactName: '',
    email: '',
    primaryPhone: '',
    secondaryPhone: '',
    address: '',
    city: '',
    zip: '',
    state: '',
    country: '',
    domainName: '',
    webLogo: '',
    mobileLogo: '',
};

export const domainRefAddVenue = { current: null as string | null, isProgress: false, isAvailable: false };

export const addVenueValidationSchema = Yup.object({
    tenantName: Yup.string().required('Venue Name is required'),
    contactName: Yup.string().required('Contact Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    domainName: Yup.string()
        .required('Domain is required')
        .min(5, 'Domain must be at least 5 characters')
        .matches(/^[a-z-]+$/, 'Domain can only contain lowercase letters and hyphens')
        .test('is-unique', 'This domain is already taken', (value) => {
            const prevUsername = domainRefAddVenue.current;
            domainRefAddVenue.current = value;

            if (value === prevUsername) {
                if (domainRefAddVenue.isAvailable) {
                    return true;
                }
                return; // Re-check username only if input value changes
            }

            return new Promise((resolve, reject) => {
                domainRefAddVenue.isAvailable = false;
                domainRefAddVenue.isProgress = true;
                checkDomainName(value)
                    ?.then(() => {
                        domainRefAddVenue.isAvailable = true;
                        resolve(true);
                    })
                    ?.catch((error) => {
                        logger.info('Failed to retrieve data in check-username', error);
                        domainRefAddVenue.isAvailable = false;
                        resolve(false);
                    })
                    ?.finally(() => {
                        domainRefAddVenue.isProgress = false;
                    });
            });
        }),
});

const checkDomainName = (domainName: string | undefined) => {
    if (!domainName) return;

    return HttpServiceHelper({
        url: checkDomainAvailabilityApi,
        method: _HttpMethods.POST,
        data: {
            domainName,
        },
    });
};

export const editVenueValidationSchema = Yup.object({
    tenantName: Yup.string().required('Venue Name is required'),
    contactName: Yup.string().required('Contact Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
});

export const addNewVenue = (
    addOrgStaffMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _VenueFormType,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    addOrgStaffMutation.mutate(
        {
            url: addVenueApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const editVenue = (
    addOrgStaffMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _VenueFormType,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    addOrgStaffMutation.mutate(
        {
            url: updateVenueApi,
            method: _HttpMethods.PUT,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const getVenueDetailByID = (
    getVenueDetailByIDMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: { id: string },
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    getVenueDetailByIDMutation.mutate(
        {
            url: getVenueDetailByIdApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const deleteVenueByID = (
    getAdminStaffDetailByIDMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: { id: string },
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    getAdminStaffDetailByIDMutation.mutate(
        {
            url: deleteVenueApi,
            method: _HttpMethods.DELETE,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export type _UpdateVenueStatus = {
    id: string;
    newStatus: boolean;
};

export const updateVenueStatus = (
    updateAdminStaffStatusMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _UpdateVenueStatus,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    updateAdminStaffStatusMutation.mutate(
        {
            url: updateVenueStatusApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export type _VenueCreationStatus = {
    ids: string[];
};

export const venueCreationStatus = (
    venueCreationStatusMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _VenueCreationStatus,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    venueCreationStatusMutation.mutate(
        {
            url: getVenueCreationStatusApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};
